import { toast } from 'react-hot-toast';
import { cn } from '@sglara/cn';
import { IconButton } from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import Copy from 'design-system/src/assets/icons/copy.svg?react';

type CopyButtonCellProps = {
  /**
   * The row data containing the value and copyValue to copy
   */
  row: CopyableDetailsTableRow;
  /**
   * The function to handle copying the value
   */
  onCopy: (value: string | React.ReactNode, copyValue?: string) => void;
};

/**
 * Cell component containing the copy button
 */
const CopyButtonCell = ({ row, onCopy }: CopyButtonCellProps) => (
  <div role="cell">
    <IconButton
      onClick={() => onCopy(row.value, row.copyValue)}
      size="sm"
      className="hover:!border-transparent hover:!bg-transparent"
    >
      <Copy title={`copy ${row.label.toLowerCase()} to clipboard`} />
    </IconButton>
  </div>
);

export type CopyableDetailsTableRow = {
  /**
   * The label for the row (displayed in the row header)
   */
  label: string;
  /**
   * The value to display in the row. Can be a string or ReactNode.
   * If showCopyButton is true and value is ReactNode, copyValue should be provided for copying.
   */
  value: string | React.ReactNode;
  /**
   * Whether to show a copy button for this row
   */
  showCopyButton?: boolean;
  /**
   * The text value to copy when value is ReactNode. If not provided and value is a string, value will be used.
   */
  copyValue?: string;
  /**
   * If true, the value will appear below the label+copy button and fill the full width of the container
   */
  fullWidthValue?: boolean;
};

export type CopyableDetailsTableProps = {
  /**
   * Class name for additional styling; used to pass down styles when using inheritance in styled components
   */
  className?: string;
  /**
   * Array of row data to display in the table
   */
  rows: CopyableDetailsTableRow[];
};

/**
 * A table component for displaying details with optional copy functionality.
 * Each row displays a label and value, and an optional copy button.
 */
export const CopyableDetailsTable = ({ className, rows }: CopyableDetailsTableProps) => {
  const handleCopy = (value: string | React.ReactNode, copyValue?: string) => {
    const textToCopy = copyValue ?? (typeof value === 'string' ? value : '');

    if (!textToCopy) {
      toast.custom(() => <StandardErrorToast />);
      return;
    }

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).then(
        () => {
          toast.custom(() => <Toast variant="success">Copied!</Toast>);
        },
        () => {
          toast.custom(() => <StandardErrorToast />);
        },
      );
    } else {
      toast.custom(() => <StandardErrorToast />);
    }
  };

  return (
    <div
      className={cn(
        'mx-auto my-6 box-border w-[600px] rounded-md border border-solid border-surface-200 bg-surface-100 px-5 py-3',
        className,
      )}
      role="table"
    >
      <div className="flex flex-col" role="rowgroup">
        {rows.map((row, index) => (
          <div
            key={`copyable-details-table-row-${row.label}`}
            className={cn(
              'flex gap-2',
              row.fullWidthValue && 'flex-col',
              index !== rows.length - 1 && 'border-grayscale-100 border-b-solid',
            )}
            role="row"
          >
            {row.fullWidthValue ? (
              <>
                <div className="flex w-full justify-between gap-2">
                  <Typography className="flex h-fit w-[120px] !py-2" role="rowheader" weight={600} renderedAs="div">
                    {row.label}
                  </Typography>
                  {row.showCopyButton && <CopyButtonCell row={row} onCopy={handleCopy} />}
                </div>
                <Typography className="w-full !pb-2 !text-grayscale-600" role="cell" renderedAs="div">
                  {row.value}
                </Typography>
              </>
            ) : (
              <>
                <Typography className="flex h-fit w-[120px] !py-2" role="rowheader" weight={600} renderedAs="div">
                  {row.label}
                </Typography>
                <Typography className="flex-1 !py-2 !text-grayscale-600" role="cell" renderedAs="div">
                  {row.value}
                </Typography>
                {row.showCopyButton && <CopyButtonCell row={row} onCopy={handleCopy} />}
                {!row.showCopyButton && <div className="h-fit" role="cell" />}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CopyableDetailsTable;
